import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { FileUploadIllustration } from "@/components/common/FileUploadIllustration/FileUploadIllustration"
import { FileUploadZone } from "@/components/common/FileUploadZone/FileUploadZone"
import { toast } from "@/components/common/Toast"
import { FreeTrialCard } from "@/components/feature/FreeTrial/FreeTrialComposed"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { PERMISSIONS } from "@/constants/permissions"
import {
  ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  ONBOARDING_UPLOAD_STEP_ID,
  useOnboarding,
} from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { useUploadQuestionnaire } from "@/services/api/questionnaireService"
import { Questionnaire } from "@/types/questionnaire/models"
import { isLimitError } from "@/utils/limitErrors"
import { IconFileSpreadsheet, IconFileText } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"

const ACCEPTED_MIME_TYPES = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/pdf": [".pdf"],
}

enum UploadState {
  Idle = "idle",
  Uploading = "uploading",
}

const QUESTIONNAIRE_FORMATS: [
  { label: string; colorClass: string },
  { label: string; colorClass: string },
] = [
  { label: "XLSX", colorClass: "text-success" },
  { label: "PDF", colorClass: "text-redLight" },
]

const UploadDescription = ({ description }: { description: string }) => (
  <>
    {description}
    <span className="mt-5 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <IconFileSpreadsheet size={12} />
        .xlsx
      </span>
      <span className="h-3 w-px bg-grey4" />
      <span className="flex items-center gap-1.5">
        <IconFileText size={12} />
        .pdf
      </span>
    </span>
  </>
)

export const UploadPage: React.FC = () => {
  const [uploadState, setUploadState] = useState<UploadState>(UploadState.Idle)
  const [uploadError, setUploadError] = useState<Error | null>(null)
  const [uploadedQuestionnaire, setUploadedQuestionnaire] =
    useState<Questionnaire | null>(null)
  const { translate } = useTranslation()

  const navigate = useNavigate()
  const { getCurrentTour, currentStepIndex, isActive } = useOnboarding()
  const { hasPermission } = useUser()
  const canCreate = hasPermission(PERMISSIONS.QUEST_CREATE)

  const isOnboardingUploadStep = useMemo(() => {
    if (!isActive) return false
    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]
    return currentStep?.id === ONBOARDING_UPLOAD_STEP_ID
  }, [currentStepIndex, getCurrentTour, isActive])

  const handleExtracted = useCallback(() => {
    if (uploadedQuestionnaire) {
      if (uploadedQuestionnaire.id === ONBOARDING_DEMO_QUESTIONNAIRE_ID) {
        navigate(
          `/questionnaire/${ONBOARDING_DEMO_QUESTIONNAIRE_ID}/excel-questions`,
          { replace: true },
        )
        return
      }
      navigate(`/questionnaire/${uploadedQuestionnaire.id}/processing`, {
        replace: true,
      })
    }
  }, [navigate, uploadedQuestionnaire])

  useEffect(() => {
    if (!uploadedQuestionnaire) {
      return
    }

    if (
      uploadedQuestionnaire.id === ONBOARDING_DEMO_QUESTIONNAIRE_ID &&
      uploadState !== UploadState.Uploading
    ) {
      handleExtracted()
      return
    }

    handleExtracted()
  }, [uploadedQuestionnaire, uploadState, handleExtracted])

  useEffect(() => {
    if (!uploadError) {
      return
    }

    trackEvent(AnalyticsEvent.UPLOAD_FAILED, {
      error_type: uploadError.name || "unknown",
    })
    // The axios interceptor already showed the limit toast with its upgrade action
    if (isLimitError(uploadError)) {
      return
    }
    toast.error(translate("QUESTIONNAIRE_UPLOAD_ZONE/TOAST_ERROR"), {
      id: "upload-error",
    })
  }, [translate, uploadError])

  return (
    <>
      <MainLayout
        contentCenter={
          <div
            data-agent-target="questionnaire.new"
            className="h-full px-S py-M"
          >
            {canCreate ? (
              <FileUploadZone
                mimeTypes={ACCEPTED_MIME_TYPES}
                getFileRejectionMessage={(file) =>
                  file.name.toLowerCase().endsWith(".xls")
                    ? translate(
                        "QUESTIONNAIRE_UPLOAD_ZONE/TOAST_XLS_NOT_SUPPORTED",
                      )
                    : null
                }
                setIsPending={(isPending) => {
                  if (isPending) {
                    setUploadState(UploadState.Uploading)
                  }
                }}
                setFiles={(files: Questionnaire[]) =>
                  setUploadedQuestionnaire(files[0] || null)
                }
                setError={(error: unknown) =>
                  setUploadError(
                    error instanceof Error ? error : new Error(String(error)),
                  )
                }
                isDisabled={isOnboardingUploadStep}
                useUploadMutation={useUploadQuestionnaire}
                idleContent={
                  <EmptyState
                    hoverable
                    illustration={
                      <FileUploadIllustration formats={QUESTIONNAIRE_FORMATS} />
                    }
                    title={translate("QUESTIONNAIRE_UPLOAD_PAGE/TITLE")}
                    description={
                      <UploadDescription
                        description={translate(
                          "QUESTIONNAIRE_UPLOAD_PAGE/DESCRIPTION",
                        )}
                      />
                    }
                  />
                }
              >
                <FreeTrialCard.QuestionnairesLimited
                  className="absolute top-10 right-10 bg-card"
                  key={uploadedQuestionnaire?.id}
                />
              </FileUploadZone>
            ) : (
              <EmptyState
                illustration={
                  <FileUploadIllustration formats={QUESTIONNAIRE_FORMATS} />
                }
                title={translate("QUESTIONNAIRE_UPLOAD_PAGE/TITLE")}
                description={
                  <UploadDescription
                    description={translate(
                      "QUESTIONNAIRE_UPLOAD_PAGE/DESCRIPTION",
                    )}
                  />
                }
              />
            )}
          </div>
        }
      />
    </>
  )
}
